import { css } from '@emotion/react';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { animated, useTransition } from '@react-spring/web';
import { useInView } from 'react-intersection-observer';
import moment from 'moment';
import t from 'react-translate';
import CollapsiblePanel from 'athena/components/collapsible-panel';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import GoalItem from 'athena/components/goal-item';
import { DEFAULT_GOAL_ITEM } from 'athena/components/goal-item/constants';
import { PanelStateKey } from 'athena/components/constants';
import useConnection from 'athena/hooks/use-connection';
import { useMentoringProgramGoals } from 'athena/hooks/use-mentoring-program-goals';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';
import { MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import { MentoringProgramGoalItem } from 'redux/schemas/models/mentoring-program-goal-items';
import {
  getMentoringProgramGoalItems,
  getMentoringProgramGoals,
} from 'redux/selectors/mentorship-program-goal-items';
import { selectGuidesForMyGoalsByRole } from 'redux/selectors/mentorship-program-guides';
import {
  headerLineHeight,
  textLargeBodyFontSize,
} from 'styles/global_defaults/fonts';
import { gray2 } from 'styles/global_defaults/colors';
import GuidanceSection from '../guidance-section';
import { config } from '@config/pendo.config.json';
import { MentoringProgramMatchingType } from 'redux/schemas/models/mentoring-programs';


const styles = css`
  font-size: ${textLargeBodyFontSize}px;
  line-height: ${headerLineHeight}px;
  color: ${gray2};
`;

const GoalsList = () => {
  const { ref: goalItemsRef, inView: isGoalsEndReached } = useInView();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const {
    id: mentorshipProgramId,
    mentorshipProgramEnrollment: currentEnrollmentId,
    matchAnnouncementDate,
    guidanceEnabled,
    matchingType,
  } = mentoringProgram || {};
  const { connection } = useConnection(currentEnrollmentId);
  const { assignedEnrollment } = connection || {};
  const { id: connectionEnrollmentId, user } = assignedEnrollment || {};
  const { firstName: connectionFirstName } = user || {};
  const { isMentor, isMentee } = useMentoringProgramRole();
  const enrollmentId = isMentee ? currentEnrollmentId : connectionEnrollmentId;
  const [editingId, setEditingId] = useState<number>();
  const isParticipantDrivenMatching = matchingType === MentoringProgramMatchingType.PARTICIPANT_DRIVEN;

  const mentoringProgramEnrollment: MentoringProgramEnrollment = useSelector(state => (
    state.models.mentoringProgramEnrollments[currentEnrollmentId]
  ));
  const { role } = mentoringProgramEnrollment || {};
  const guides = useSelector(selectGuidesForMyGoalsByRole(role.name));

  const {
    loadMore,
  }: {
    loadMore: () => void;
  } = useMentoringProgramGoals({ mentorshipProgramId, enrollmentId });

  const { totalCount } = useSelector(getMentoringProgramGoalItems(enrollmentId)) || {};
  const goalItems = useSelector(getMentoringProgramGoals(enrollmentId)) || [];
  const getDefaultGoalItem = () => ({ ...DEFAULT_GOAL_ITEM });

  const animationConfig = {
    keys: (item) => item.id,
    from: { opacity: 0, height: 0 },
    enter: { opacity: 1, height: 'auto' },
    leave: { opacity: 0, height: 0 },
    config: { duration: 300 },
  };

  const goalTransitions = useTransition(
    [isMentor ? null : getDefaultGoalItem(), ...(goalItems || [])].filter(
      Boolean,
    ),
    animationConfig,
  );

  useEffect(() => {
    if (isGoalsEndReached) {
      loadMore();
    }
  }, [isGoalsEndReached, loadMore]);

  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GOALS;
  const panelTitle = isMentee
    ? translationBase.MENTEE_TITLE(totalCount)
    : translationBase.MENTOR_TITLE(connectionFirstName, totalCount);

  const toGoalItem = (item: MentoringProgramGoalItem) => (
    <GoalItem
      isReadOnly={isMentor}
      item={item}
      isEditing={editingId === item.id}
      setEditingId={setEditingId}
    />
  );

  const announcementDateHasPassed = moment().isAfter(
    moment(matchAnnouncementDate),
  );

  const renderForCurrentRole = isMentee
    ? true
    : connection && (announcementDateHasPassed || isParticipantDrivenMatching);

  const emptyState = isMentor && !goalItems.length ? (
    <div css={styles} className='my-4 text-align-center semi-bold'>
      {translationBase.EMPTY_LIST(connectionFirstName)}
    </div>
  ) : null;

  return renderForCurrentRole ? (
    <CollapsiblePanel
      title={panelTitle}
      panelState={{
        persistState: true,
        localStorageKey: PanelStateKey.GOAL_ITEMS_STATE,
      }}
      ariaLabel={panelTitle}
      dataQa={config.pendo.athena.mentorshipProgram.participantHome.goals.list.goalsPanel}
    >
      <>
        <div className='mt-2 mb-4'>
          {goalTransitions((style, item) => (
            <animated.div style={style}>{toGoalItem(item)}</animated.div>
          ))}
          <div ref={goalItemsRef} />
        </div>
        {emptyState}
        {guidanceEnabled && (
          <GuidanceSection guides={guides} />
        )}
      </>
    </CollapsiblePanel>
  ) : null;
};

export default GoalsList;
